<template>
  <section class="sidebar-card">
    <button type="button" class="card-header card-toggle" :class="{ 'is-static': forceOpen }"
      :aria-expanded="forceOpen || open" @click="forceOpen ? null : (open = !open)">
      <div class="card-heading">
        <div class="card-eyebrow">Visibility</div>
        <h3 class="card-title">Layers</h3>
      </div>
      <v-icon v-if="!forceOpen" size="20" color="#8aa0aa">{{ open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </button>

    <div v-show="forceOpen || open" class="card-body toggle-list">
      <button type="button" class="toggle-row" :class="{ 'is-on': !anomaliesHidden }" @click="toggleAnomalies">
        <v-icon size="18" :color="anomaliesHidden ? '#5a6970' : '#3b82f6'">
          {{ anomaliesHidden ? 'mdi-eye-off-outline' : 'mdi-eye' }}
        </v-icon>
        <span class="toggle-label">Anomalies</span>
        <span class="toggle-state">{{ anomaliesHidden ? 'Hidden' : 'Shown' }}</span>
      </button>

      <button type="button" class="toggle-row" :class="{ 'is-on': !hotspotsHidden }" @click="toggleHotspots">
        <v-icon size="18" :color="hotspotsHidden ? '#5a6970' : '#ef4444'">
          {{ hotspotsHidden ? 'mdi-fire-off' : 'mdi-fire-alert' }}
        </v-icon>
        <span class="toggle-label">Hotspots</span>
        <span class="toggle-state">{{ hotspotsHidden ? 'Hidden' : 'Shown' }}</span>
      </button>

      <button type="button" class="toggle-row" :class="{ 'is-on': !walkingPathHidden }" @click="toggleWalkingPath">
        <v-icon size="18" :color="walkingPathHidden ? '#5a6970' : '#64ffda'">
          {{ walkingPathHidden ? 'mdi-vector-polyline' : 'mdi-vector-polyline' }}
        </v-icon>
        <span class="toggle-label">Walking path</span>
        <span class="toggle-state">{{ walkingPathHidden ? 'Hidden' : 'Shown' }}</span>
      </button>

      <button type="button" class="toggle-row" :class="{ 'is-on': !zonesHidden }" @click="toggleZones">
        <v-icon size="18" :color="zonesHidden ? '#5a6970' : '#64ffda'">
          {{ zonesHidden ? 'mdi-cube-off-outline' : 'mdi-cube-outline' }}
        </v-icon>
        <span class="toggle-label">Zones</span>
        <span class="toggle-state">{{ zonesHidden ? 'Hidden' : 'Shown' }}</span>
      </button>

      <button type="button" class="toggle-row" :class="{ 'is-on': !cameraHelpersHidden }" @click="toggleCameraHelpers">
        <v-icon size="18" :color="cameraHelpersHidden ? '#5a6970' : '#a78bfa'">
          {{ cameraHelpersHidden ? 'mdi-camera-off-outline' : 'mdi-camera-outline' }}
        </v-icon>
        <span class="toggle-label">Cameras</span>
        <span class="toggle-state">{{ cameraHelpersHidden ? 'Hidden' : 'Shown' }}</span>
      </button>
    </div>
  </section>
</template>

<script setup>
import { computed, defineOptions, defineProps, ref } from 'vue';
import { useViewerUiStore } from '@/store/viewerUiStore';

defineOptions({ name: 'LayerVisibilityCard' });

// When hosted in the viewer tool rail the card is always expanded (the rail icon is the toggle).
defineProps({ forceOpen: { type: Boolean, default: false } });

const viewerUi = useViewerUiStore();

const open = ref(false);

// Layer visibility lives in viewerUiStore (single source of truth). These computed aliases
// keep the template's *Hidden bindings readable while reading the store.
const anomaliesHidden = computed(() => !viewerUi.layers.anomalies);
const hotspotsHidden = computed(() => !viewerUi.layers.hotspots);
const zonesHidden = computed(() => !viewerUi.layers.zones);
const cameraHelpersHidden = computed(() => !viewerUi.layers.cameras);
const walkingPathHidden = computed(() => !viewerUi.layers.walkingPath);

const toggleAnomalies = () => viewerUi.toggleLayer('anomalies');
const toggleHotspots = () => viewerUi.toggleLayer('hotspots');
const toggleZones = () => viewerUi.toggleLayer('zones');
const toggleCameraHelpers = () => viewerUi.toggleLayer('cameras');
const toggleWalkingPath = () => viewerUi.toggleLayer('walkingPath');
</script>

<style scoped>
.sidebar-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.card-header {
  padding: 12px 14px 8px;
}

/* Collapsible header acts as the accordion toggle */
.card-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  background: transparent;
  border: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  padding-bottom: 12px;
}

.card-toggle:hover .card-title {
  color: var(--duallo-accent);
}

.card-toggle.is-static { cursor: default; }
.card-toggle.is-static:hover .card-title { color: #ffffff; }

.card-heading {
  min-width: 0;
}

.card-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 2px 0 0;
  color: #ffffff;
}

.card-body {
  padding: 4px 12px 12px;
}

.toggle-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid transparent;
  border-radius: 8px;
  color: #cfd8dc;
  font: inherit;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.toggle-row:hover {
  background: rgba(255, 255, 255, 0.04);
}

.toggle-row.is-on {
  background: rgba(100, 255, 218, 0.06);
  border-color: rgba(100, 255, 218, 0.15);
}

.toggle-label {
  flex: 1 1 auto;
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffffff;
}

.toggle-state {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.toggle-row.is-on .toggle-state {
  color: var(--duallo-accent);
}
</style>
